import { PUBLIC_URL } from 'config';
import { sortBy } from 'lodash-es';
import computeTimezoneOffset from 'utils/functions/computeTimezoneOffset';
import { createScopedErrorHandler } from 'utils/functions/handleError';
import htmlUnescape from 'utils/functions/htmlUnescape';
import {
  InboxMessage,
  ModifyMessageInfo,
  SGADInboxMessage,
} from './inbox.types';

export const inboxErrorHandler = createScopedErrorHandler('inbox');

/**
 * Returns 'true' if the message is high pririty.
 * @param @see InboxMessage
 */
export function hasEmailHighPriority(message: InboxMessage): boolean {
  return message.computedData.isHighPriority;
}

/**
 * Returns the natural order of the InboxMessage type according to the following
 * criteria:
 * sort by priority first then sort by date.
 * @param thisMessage an InboxMessage.
 * @param thatMessage an other InboxMessage.
 * @return {number}
 */
export function inboxMessageComparator(
  thisMessage: InboxMessage,
  thatMessage: InboxMessage,
): number {
  const isThisMessageHighPriority = hasEmailHighPriority(thisMessage);
  const isThatMessageHighPriority = hasEmailHighPriority(thatMessage);

  if (isThisMessageHighPriority && !isThatMessageHighPriority) {
    return 1;
  }

  if (!isThisMessageHighPriority && isThatMessageHighPriority) {
    return -1;
  }

  if (thisMessage.insertDate === thatMessage.insertDate) {
    return 0;
  }

  return thisMessage.insertDate > thatMessage.insertDate ? 1 : -1;
}

/**
 * Returns the comparator of the descending order of the `InboxMessage` type.
 * @param thisMessage an `InboxMessage`.
 * @param thatMessage an other `InboxMessage`.
 * @return {number}
 */
export function inboxMessageComparatorDescendingOrder(
  thisMessage: InboxMessage,
  thatMessage: InboxMessage,
): number {
  const comp = inboxMessageComparator(thisMessage, thatMessage);

  if (comp) {
    return -comp;
  }
  // We avoid IEEE 754 `-0` in order to provide the same result for the
  // Algorithms:
  // 1. Same value according to `Object.is`
  // 2. Strict equality `===`.
  // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness
  return comp;
}

export function getRemoveMessageResourceId(message: InboxMessage): string {
  return `remove-inbox-message-${message.idMessage}`;
}

export function getBulkEditMessageInfoResourceId(
  patches: ModifyMessageInfo[],
): string {
  return `edit-message-infos-of-${JSON.stringify(
    sortBy(patches, (patch) => patch.idMessage),
  )}`;
}

/**
 * Returns true if the message is deemed `vip`.
 * @param message An inbox message
 */
export function isVipMessage(message: InboxMessage): boolean {
  // From what we have managed to figure out looking at
  // `https://test-www.sisal.it/areaprivata/sisal/inbox` and comparing  with
  // the api data, a message is `VIP` if its sender includes the string `vip`.
  return /\bvip\b/i.test(message.sender);
}

function parseBase64HtmlBody(base64HtmlString: string | undefined): string {
  return base64HtmlString ? window.atob(base64HtmlString) : '';
}

type InboxMessageTransformer = (DOMDocument: Document) => void;

/**
 * Replaces default link target with `_parent`:
 * We got asked to enable changing parent page from iframe.
 * @param DOMDocument
 */
function transformLinks(DOMDocument: Document) {
  const allLinks =
    DOMDocument.documentElement?.querySelectorAll('a[href]') || [];

  for (let i = 0, len = allLinks.length; i < len; i++) {
    const link = allLinks[i] as HTMLAnchorElement;

    // Enables links in iframe to change page of parent
    if (!link.target || link.target === '_self') {
      link.target = '_parent';
    }
  }
}

/**
 * Adds css base styles used in the main site.
 * @param DOMDocument
 */
function addBaseCssStyles(DOMDocument: Document) {
  let head = DOMDocument.querySelector('head');

  // Adds head if not present
  if (!head) {
    head = DOMDocument.createElement('head');
    DOMDocument.documentElement?.appendChild(head);
  }

  // We use this class to set iframe only styles.
  DOMDocument.documentElement?.classList.add('iframe-context');

  const baseCssStyles = ['normalize8.0.1.css', 'css-baseline1.0.0.css'];

  for (let i = 0, len = baseCssStyles.length; i < len; i++) {
    const styleSheetLink = DOMDocument.createElement('link');
    styleSheetLink.rel = 'stylesheet';

    styleSheetLink.href = `${PUBLIC_URL}/${baseCssStyles[i]}`;

    head.appendChild(styleSheetLink);
  }
}

/**
 * Applies to the Document provided the transformations passed as argument and
 * returns the transformed document.
 * @param domParsedInstance @see https://developer.mozilla.org/en-US/docs/Web/API/DOMParser
 * @param parsedHtmlString a 'text/html' string
 * @param transformations @see InboxMessageTransformer
 */
function htmlTransformations(
  domParsedInstance: DOMParser,
  parsedHtmlString: string,
  transformations: InboxMessageTransformer[],
): string {
  if (!parsedHtmlString) {
    return '';
  }

  const documentGenerated = domParsedInstance.parseFromString(
    parsedHtmlString,
    'text/html',
  );

  for (let i = 0, len = transformations.length; i < len; i++) {
    transformations[i](documentGenerated);
  }

  return documentGenerated.documentElement.outerHTML;
}

const domParserRef = {
  current: null as null | DOMParser,
};

const getDomParserInstance = (): DOMParser => {
  if (!domParserRef.current) {
    domParserRef.current = new DOMParser();
  }

  return domParserRef.current;
};

export function transformInboxMessageText(
  base64HtmlString: string | undefined,
) {
  const parsedHtmlBody = parseBase64HtmlBody(base64HtmlString);

  if (!parsedHtmlBody) {
    return '';
  }

  try {
    return htmlTransformations(getDomParserInstance(), parsedHtmlBody, [
      transformLinks,
      addBaseCssStyles,
    ]);
  } catch (htmlError) {
    inboxErrorHandler(htmlError);

    return '';
  }
}

export type PatchInboxMessageOptions = {
  nowEpochTime: number;
  timezoneOffset: number;
};

/**
 * Returns a copy of the incoming message patched.
 * @param inboxMessage
 */
export function patchApiInboxMessage(
  inboxMessage: SGADInboxMessage,
  { nowEpochTime, timezoneOffset }: PatchInboxMessageOptions,
): InboxMessage {
  const oneDay = 86_400_000;
  const nowAsDateInstance = new Date(inboxMessage.insertDate);
  const insertDateEpoch = nowAsDateInstance.getTime();

  const timezoneOffsetBetweenUTCAndRome = computeTimezoneOffset(
    nowAsDateInstance,
    'Europe/Rome',
  );

  const isHighPriority =
    oneDay * (inboxMessage.notificationGGPriority ?? 0) + insertDateEpoch >=
    nowEpochTime;

  let arrivedAt: string;
  let promotionEndsAt: string | undefined = undefined;

  if (timezoneOffsetBetweenUTCAndRome) {
    arrivedAt = new Date(
      new Date(inboxMessage.insertDate).getTime() +
        timezoneOffsetBetweenUTCAndRome * 60_000,
    ).toISOString();

    if (inboxMessage.promotionalTypeEndDate) {
      promotionEndsAt = new Date(
        new Date(inboxMessage.promotionalTypeEndDate).getTime() +
          timezoneOffsetBetweenUTCAndRome * 60_000,
      ).toISOString();
    }
  } else {
    promotionEndsAt = inboxMessage.promotionalTypeEndDate;
    arrivedAt = inboxMessage.insertDate;
  }

  return {
    ...inboxMessage,
    computedData: {
      isHighPriority: isHighPriority,
      // For some reason we receive from 'SGAD'
      // a 'subject' that has been html escaped twice.
      subject: htmlUnescape(htmlUnescape(inboxMessage.matter)),
      htmlBody: transformInboxMessageText(inboxMessage.text),
      arrivedAt,
      promotionEndsAt,
    },
  };
}
